import { useReducer, useState } from "react";
import { useApp } from "../../../../../context/AppContext";
import type { CalendarEventType } from "../../../../../types/calendar";
import type { SalonService } from "../../../../../types/salon";
import type {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../../../types/transactions";
import { getCreatedTransactions } from "../../../../../utils/bookingResponse";
import {
  BookingDraftState,
  bookingDraftReducer,
  initialBookingDraft,
} from "../../booking/bookingDraftReducer";
import {
  rebookStartOffsets,
  seedFromTransaction,
  sourceStartTime,
} from "../../booking/bookingSeed";
import {
  applySuggestedSlot,
  buildBookingPayload,
  isDraftBookable,
  rowsAtStart,
  suggestionQuery,
  toMinutes,
} from "../../booking/draftModel";
import type { SuggestedSlot } from "../../booking/useSuggestedTimes";
import {
  ToastCopy,
  addDaysToDay,
  bookingToastCopy,
  draftTotals,
  rowConflict,
} from "./bookingSheetModel";

interface UseMobileRebookArgs {
  transaction: GetTransactionsResponse;
  /** Salon services (servicing_staff: providers, price / durations). */
  services: SalonService[];
  timezone: string;
  events: CalendarEventType[];
}

/**
 * The rebook screen's draft. Rows are seeded by seedFromTransaction
 * ("rebook": same services, providers, durations, prices and adjustment
 * answers) and changed through bookingDraftReducer, which owns the picked
 * day (`dayChanged`, single-day rule as the drawer); a new start time moves every service together, keeping their
 * current spacing. Suggested times follow the drawer's rule
 * (draftModel.suggestionQuery): every service in order with its provider
 * and the first start; a picked slot sets each service's own start. Confirm is the desktop rebook write (buildBookingPayload
 * "rebook": unit_price + rebooking_source_uuid).
 */
export function useMobileRebook({
  transaction,
  services,
  timezone,
  events,
}: UseMobileRebookArgs) {
  const { createAppointment } = useApp();

  const [seed] = useState(() => {
    const s = seedFromTransaction(transaction, "rebook", timezone, new Date());
    return {
      ...s,
      offsets: rebookStartOffsets(transaction, timezone),
      time: sourceStartTime(transaction, timezone),
    };
  });
  const [draft, dispatch] = useReducer(
    bookingDraftReducer,
    seed,
    (s): BookingDraftState =>
      bookingDraftReducer(initialBookingDraft, {
        type: "open",
        mode: "rebook",
        origin: "calendar",
        sourceTxn: transaction,
        rows: rowsAtStart(s.rows, s.offsets, s.time),
        clientUuid: s.clientUuid,
        // Design default: one week after the source appointment.
        day: addDaysToDay(s.day, 7),
      }),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { rows, day } = draft;
  const time = rows[0]?.time || "";
  const bookable = isDraftBookable(rows, draft.clientUuid, services);
  const clash = rows.some((r) => !!rowConflict(events, r));
  const totals = draftTotals(rows, (r) => r.price || 0);

  const sugQuery = suggestionQuery(rows);

  const setDate = (next: string) => {
    if (next) dispatch({ type: "dayChanged", day: next });
  };
  const setTime = (next: string) => {
    if (!next) return;
    dispatch({
      type: "rowsUpdated",
      // Keep the services' current spacing (the source's, or a picked
      // suggested time's).
      update: (prev) => {
        const firstTime = prev[0]?.time;
        const offsets = firstTime
          ? prev.map((r, i) =>
              r.time
                ? toMinutes(r.time) - toMinutes(firstTime)
                : seed.offsets[i] || 0,
            )
          : seed.offsets;
        return rowsAtStart(prev, offsets, next);
      },
    });
  };
  const applySuggestion = (slot: SuggestedSlot) => {
    // The slot's date on the rows moves the draft's day too (single day).
    dispatch({
      type: "rowsUpdated",
      update: (prev) =>
        applySuggestedSlot(prev, sugQuery, slot, timezone, services),
    });
  };

  /** Create the rebooked appointment; the created transactions on success. */
  const confirm = async (
    clientName: string,
  ): Promise<{ created: TransactionResponse[]; toast: ToastCopy } | null> => {
    if (!bookable || clash || submitting) return null;
    setSubmitting(true);
    setError(null);
    try {
      const response = await createAppointment(
        buildBookingPayload({
          rows,
          clientUuid: draft.clientUuid,
          mode: "rebook",
          sourceTransactionId: transaction.transaction.id,
          timezone,
          services,
        }),
      );
      return {
        created: getCreatedTransactions(response),
        toast: bookingToastCopy(rows.length, clientName, totals),
      };
    } catch (e) {
      setError(
        (e instanceof Error && e.message) ||
          "Could not create the appointment.",
      );
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  return {
    sourceDay: seed.day,
    sourceTime: seed.time,
    day,
    time,
    rows,
    bookable,
    clash,
    totals,
    submitting,
    error,
    /** GET /suggested-times query (all services, providers, start). */
    sugQuery,
    setDate,
    setTime,
    applySuggestion,
    confirm,
  };
}
